<script setup lang="ts">
	import {useCookieConsentStore} from '@/scripts/stores/cookie-consent';
	import {trans} from 'laravel-vue-i18n';
	import {Button} from '@/views/components/ui/button';
	import {
		Dialog,
		DialogContent,
		DialogDescription,
		DialogHeader,
		DialogTitle,
		DialogFooter,
	} from '@/views/components/ui/dialog';
	import {Switch} from '@/views/components/ui/switch';
	import {
		Card,
		CardContent,
		CardDescription,
		CardHeader,
		CardTitle,
	} from '@/views/components/ui/card';
	import {Badge} from '@/views/components/ui/badge';
	import {
		Shield,
		BarChart3,
		Settings,
		Megaphone,
		Info,
		ExternalLink,
		Clock,
	} from 'lucide-vue-next';
	import {computed} from 'vue';

	const cookieStore = useCookieConsentStore();

	const categoryIcons = {
		necessary: Shield,
		analytics: BarChart3,
		functional: Settings,
		marketing: Megaphone,
	};

	const categoryColors = {
		necessary: 'bg-blue-500',
		analytics: 'bg-green-500',
		functional: 'bg-purple-500',
		marketing: 'bg-orange-500',
	};

	const enabledCount = computed(
		() => cookieStore.categories.filter((c) => c.enabled).length
	);

	const totalCount = computed(() => cookieStore.categories.length);

	const formatLastUpdated = computed(() => {
		if (!cookieStore.lastUpdated) return null;
		return new Intl.DateTimeFormat('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		}).format(cookieStore.lastUpdated);
	});
</script>

<template>
	<Dialog
		:open="cookieStore.showSettings"
		@update:open="cookieStore.closeSettings"
	>
		<DialogContent class="max-h-[80vh] max-w-2xl overflow-y-auto">
			<DialogHeader>
				<DialogTitle class="flex items-center gap-3">
					<div
						class="bg-primary/10 flex h-8 w-8 items-center justify-center rounded-full"
					>
						<Settings class="text-primary h-4 w-4" />
					</div>
					{{ trans('cookie_consent.settings.title') }}
				</DialogTitle>
				<DialogDescription>
					{{ trans('cookie_consent.settings.description') }}
				</DialogDescription>
			</DialogHeader>

			<div class="space-y-6">
				<!-- Status overview -->
				<Card>
					<CardHeader class="pb-3">
						<CardTitle class="flex items-center gap-2 text-base">
							<Info class="h-4 w-4" />
							{{
								trans('cookie_consent.settings.status_overview')
							}}
						</CardTitle>
					</CardHeader>
					<CardContent class="space-y-3">
						<div class="flex items-center justify-between">
							<span class="text-sm">{{
								trans(
									'cookie_consent.settings.enabled_categories'
								)
							}}</span>
							<Badge variant="secondary"
								>{{ enabledCount }}/{{ totalCount }}</Badge
							>
						</div>

						<div
							v-if="cookieStore.lastUpdated"
							class="text-muted-foreground flex items-center justify-between text-xs"
						>
							<span class="flex items-center gap-1">
								<Clock class="h-3 w-3" />
								{{
									trans(
										'cookie_consent.settings.last_updated'
									)
								}}
							</span>
							<span>{{ formatLastUpdated }}</span>
						</div>

						<div class="text-muted-foreground text-xs">
							{{
								trans('cookie_consent.settings.consent_expires')
							}}
						</div>
					</CardContent>
				</Card>

				<!-- Cookie categories -->
				<div class="space-y-4">
					<h3 class="text-sm font-medium">
						{{ trans('cookie_consent.settings.categories_title') }}
					</h3>

					<div class="space-y-3">
						<Card
							v-for="category in cookieStore.categories"
							:key="category.id"
							:class="[
								'transition-all duration-200',
								category.enabled
									? 'ring-primary/20 bg-primary/5 ring-1'
									: '',
							]"
						>
							<CardHeader class="pb-3">
								<div class="flex items-center justify-between">
									<div class="flex items-center gap-3">
										<div
											:class="[
												'flex h-8 w-8 items-center justify-center rounded-full',
												category.enabled
													? categoryColors[
															category.id as keyof typeof categoryColors
														]
													: 'bg-muted',
											]"
										>
											<component
												:is="
													categoryIcons[
														category.id as keyof typeof categoryIcons
													]
												"
												class="h-4 w-4 text-white"
											/>
										</div>
										<div>
											<CardTitle class="text-sm">
												{{
													trans(
														`cookie_consent.categories.${category.id}.name`
													)
												}}
												<Badge
													v-if="category.required"
													variant="secondary"
													class="ml-2 text-xs"
												>
													{{
														trans(
															'cookie_consent.settings.required'
														)
													}}
												</Badge>
											</CardTitle>
										</div>
									</div>
									<div class="flex items-center space-x-2">
										<Switch
											:id="`category-${category.id}`"
											:model-value="category.enabled"
											:disabled="category.required"
											@update:model-value="
												(value) => {
													if (!category.required) {
														category.enabled =
															value;
													}
												}
											"
										/>
									</div>
								</div>
							</CardHeader>

							<CardContent>
								<CardDescription
									class="text-xs leading-relaxed"
								>
									{{
										trans(
											`cookie_consent.categories.${category.id}.description`
										)
									}}
								</CardDescription>

								<!-- Show specific services for analytics -->
								<div
									v-if="
										category.id === 'analytics' &&
										category.enabled
									"
									class="bg-muted/50 mt-3 rounded-md p-2"
								>
									<div class="mb-1 text-xs font-medium">
										{{
											trans(
												'cookie_consent.settings.services_included'
											)
										}}
									</div>
									<div
										class="text-muted-foreground space-y-1 text-xs"
									>
										<div
											class="flex items-center justify-between"
										>
											<span>PostHog Analytics</span>
											<Badge
												variant="outline"
												class="text-xs"
												>{{
													trans(
														'cookie_consent.settings.active'
													)
												}}</Badge
											>
										</div>
										<div
											class="flex items-center justify-between"
										>
											<span>Sentry Error Tracking</span>
											<Badge
												variant="outline"
												class="text-xs"
												>{{
													trans(
														'cookie_consent.settings.active'
													)
												}}</Badge
											>
										</div>
									</div>
								</div>
							</CardContent>
						</Card>
					</div>
				</div>

				<!-- Additional information -->
				<Card>
					<CardHeader class="pb-3">
						<CardTitle class="text-base">
							{{ trans('cookie_consent.settings.your_rights') }}
						</CardTitle>
					</CardHeader>
					<CardContent class="space-y-3">
						<div class="text-muted-foreground space-y-2 text-xs">
							<p>
								{{
									trans(
										'cookie_consent.settings.rights_description'
									)
								}}
							</p>
							<ul class="ml-2 list-inside list-disc space-y-1">
								<li>
									{{
										trans(
											'cookie_consent.settings.right_withdraw'
										)
									}}
								</li>
								<li>
									{{
										trans(
											'cookie_consent.settings.right_access'
										)
									}}
								</li>
								<li>
									{{
										trans(
											'cookie_consent.settings.right_portability'
										)
									}}
								</li>
								<li>
									{{
										trans(
											'cookie_consent.settings.right_erasure'
										)
									}}
								</li>
							</ul>
						</div>

						<div class="flex items-center gap-4 pt-2 text-xs">
							<RouterLink
								:href="route('privacy_policy')"
								class="text-primary flex items-center gap-1 hover:underline"
							>
								{{
									trans(
										'cookie_consent.settings.privacy_policy'
									)
								}}
								<ExternalLink class="h-3 w-3" />
							</RouterLink>
							<RouterLink
								:href="route('legal_notice')"
								class="text-primary flex items-center gap-1 hover:underline"
							>
								{{
									trans(
										'cookie_consent.settings.legal_notice'
									)
								}}
								<ExternalLink class="h-3 w-3" />
							</RouterLink>
						</div>
					</CardContent>
				</Card>
			</div>

			<DialogFooter class="gap-2 pt-6">
				<Button
					@click="cookieStore.acceptNecessary"
					variant="outline"
				>
					{{ trans('cookie_consent.settings.accept_necessary') }}
				</Button>

				<Button @click="cookieStore.saveConsent">
					{{ trans('cookie_consent.settings.save_preferences') }}
				</Button>
			</DialogFooter>
		</DialogContent>
	</Dialog>
</template>
